import React, { useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import Select from '@/components/common/ReactSelect5';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import Text from '@/components/ui/Text';
import { RequiredAsterisk } from '@/components/common/Forms/Utils';
import * as Routes from '@/modules/routes';
import setupCsrfToken from '@/modules/SetupCSRFToken';
import { QTI_DOWNLOAD_RECORDED_EVENT } from './constants';

const SEARCH_DEBOUNCE_MS = 400;

interface SubscriberOption {
  label: string;
  value: number;
}

interface SearchedSubscriber {
  id: number;
  name: string;
}

interface SelectSubscriberModalProps {
  close: () => void;
  createDownloadUrl: string;
  isOpen: boolean;
  packageId: number;
  onDownload: () => void;
}

const SelectSubscriberModal = ({
  close,
  createDownloadUrl,
  isOpen,
  packageId,
  onDownload,
}: SelectSubscriberModalProps) => {
  const [options, setOptions] = useState<SubscriberOption[]>([]);
  const [selected, setSelected] = useState<SubscriberOption | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const isMounted = useRef(true);
  const requestIdRef = useRef(0);
  const initialOptionsRef = useRef<SubscriberOption[]>([]);

  const fetchOptions = (input: string) => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const searchParams = {
      only: ['id', 'name'],
      search: { name_or_id_string_cont: input },
    };
    Axios
      .post<{ data: SearchedSubscriber[] }>(Routes.plato_api_search_subscribers_path(), searchParams)
      .then(({ data }) => {
        if (!isMounted.current || requestId !== requestIdRef.current) return;
        const fetched = data.data.map(({ id, name }) => ({ label: `${name} (${id})`, value: id }));
        if (!input) initialOptionsRef.current = fetched;
        setOptions(fetched);
        setSearchFailed(false);
      })
      .catch(() => {
        if (!isMounted.current || requestId !== requestIdRef.current) return;
        setOptions([]);
        setSearchFailed(true);
      })
      .finally(() => {
        if (!isMounted.current || requestId !== requestIdRef.current) return;
        setIsSearching(false);
      });
  };

  // The modal is conditionally mounted, so closing it mid-debounce or mid-request must not
  // leave a pending timer or update state on an unmounted component.
  useEffect(() => () => {
    isMounted.current = false;
    clearTimeout(debounceRef.current);
  }, []);

  // Mount === open, so this seeds the select with the first page of subscribers before any typing.
  useEffect(() => {
    setIsSearching(true);
    fetchOptions('');
  }, []);

  const searchSubscribers = (input: string, { action }: { action: string }) => {
    if (action !== 'input-change') return;

    clearTimeout(debounceRef.current);
    if (!input.trim()) {
      requestIdRef.current += 1;
      setOptions(initialOptionsRef.current);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(() => fetchOptions(input.trim()), SEARCH_DEBOUNCE_MS);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;

    setupCsrfToken();
    setIsSubmitting(true);
    setHasError(false);
    Axios
      .post<{ subscriber_count: number }>(createDownloadUrl, { subscriber_id: selected.value })
      .then(({ data }) => {
        // AIDEV-NOTE: the event carries the count because the download navigation in onDownload
        // cancels in-flight XHRs in real browsers — listeners must not refetch in response.
        window.dispatchEvent(new CustomEvent(QTI_DOWNLOAD_RECORDED_EVENT, {
          detail: { packageId, subscriberCount: data.subscriber_count },
        }));
        onDownload();
        close();
      })
      .catch(() => {
        setHasError(true);
        setIsSubmitting(false);
      });
  };

  return (
    <SubmitModal
      allowOverflow
      close={close}
      headerText="Download QTI Package"
      isLoading={isSubmitting}
      isOpen={isOpen}
      isValid={!!selected}
      submitText="Download"
      submittingText="Downloading…"
      onSubmit={handleSubmit}
    >
      <Text>
        Select the district this download is for. TCI records which districts use each package.
      </Text>
      <div className="tw-mt-4 tw-mb-6">
        <Text htmlFor="qti-subscriber-select" size="sm" tag="label" weight="bold">
          Subscriber <RequiredAsterisk />
        </Text>
        <Select
          filterOption={() => true}
          inputId="qti-subscriber-select"
          isLoading={isSearching}
          noOptionsMessage={({ inputValue }) => {
            if (searchFailed) return 'Something went wrong searching subscribers. Please try again.';

            return inputValue ? 'No subscribers found' : 'Search by subscriber name or ID';
          }}
          options={options}
          placeholder="Search by subscriber name or ID"
          value={selected}
          onChange={newValue => setSelected(newValue as SubscriberOption | null)}
          onInputChange={searchSubscribers}
        />
      </div>
      {hasError && (
        <Text className="tw-text-red tw-mb-4" size="sm">
          Something went wrong recording the download. Please try again.
        </Text>
      )}
    </SubmitModal>
  );
};

export default SelectSubscriberModal;
